/**
 * Base API client configuration
 */
import { ApiError, TokenResponse } from '@/types';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { resolveApiBaseUrl } from './apiBaseUrl';

// Get API base URL from environment variable.
// Default to same-origin (works behind nginx and locally if you proxy /api/v1).
const API_BASE_URL = resolveApiBaseUrl();

class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
  }> = [];

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Enable sending cookies
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    // Cookies are automatically sent by browser, but we also support Authorization header for backward compatibility
    this.client.interceptors.request.use(
      (config) => {
        // Re-resolve on every request so HMR / stale env cannot keep Docker-internal URLs.
        config.baseURL = resolveApiBaseUrl();

        // Try to get token from cookie first (preferred method)
        // If not available, try localStorage for backward compatibility
        const accessToken = this.getCookie('access_token') || localStorage.getItem('access_token');
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        // Note: Cookies are automatically sent by browser with credentials: 'include'
        // Always set withCredentials to ensure cookies are sent
        config.withCredentials = true;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors and token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't try to refresh token for the refresh endpoint itself
        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh')) {
          console.log('[Token Refresh] Received 401, attempting to refresh token for:', originalRequest.url);
          // If we're already refreshing, queue this request
          if (this.isRefreshing) {
            console.log('[Token Refresh] Already refreshing, queuing request');
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return this.client(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          console.log('[Token Refresh] Starting token refresh process');
          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            // Try to get refresh token from cookie first, then localStorage for backward compatibility
            const refreshToken = this.getCookie('refresh_token') || localStorage.getItem('refresh_token');
            if (!refreshToken) {
              // No refresh token, redirect to login
              this.clearAuthTokens();
              this.processQueue(null, new Error('No refresh token'));
              if (window.location.pathname !== '/auth') {
                window.location.href = '/auth';
              }
              return Promise.reject(error);
            }

            const newTokens = await this.refreshToken(refreshToken);

            // Update stored tokens (cookies are set by server, but we also update localStorage for backward compatibility)
            if (newTokens.access_token) {
              localStorage.setItem('access_token', newTokens.access_token);
            }
            if (newTokens.refresh_token) {
              localStorage.setItem('refresh_token', newTokens.refresh_token);
            }

            // Process queued requests
            this.processQueue(newTokens.access_token, null);

            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${newTokens.access_token}`;
            return this.client(originalRequest);
          } catch (refreshError: any) {
            console.error('Token refresh failed:', refreshError);
            // Refresh failed, redirect to login
            this.clearAuthTokens();
            this.processQueue(null, refreshError);
            // Only redirect if we're not already on the auth page
            if (window.location.pathname !== '/auth') {
              // Use setTimeout to avoid blocking
              setTimeout(() => {
                window.location.href = '/auth';
              }, 100);
            }
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        // Log error details for debugging
        console.error('[API Error]', {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          headers: error.response?.headers,
          message: error.message,
        });

        // Transform error to our format
        const apiError: ApiError = {
          detail: error.response?.data?.detail || error.message || 'An error occurred',
          status_code: error.response?.status || 500,
        };

        return Promise.reject(apiError);
      }
    );
  }

  private processQueue(token: string | null, error: Error | null) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  private getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  private clearAuthTokens() {
    // Clear localStorage tokens
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    // Note: Cookies are cleared by server on logout endpoint
  }

  private async refreshToken(refreshToken: string): Promise<TokenResponse> {
    try {
      console.log('[Token Refresh] Attempting to refresh token...');
      const response = await axios.post(
        `${resolveApiBaseUrl()}/auth/refresh`,
        { refresh_token: refreshToken },
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true, // Ensure cookies are sent
          timeout: 10000 // 10 seconds timeout
        }
      );
      console.log('[Token Refresh] Token refreshed successfully');
      return response.data;
    } catch (error: any) {
      console.error('[Token Refresh] Request failed:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: `${resolveApiBaseUrl()}/auth/refresh`
      });
      // If it's a network error or timeout, throw a more descriptive error
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('Token refresh request timed out');
      }
      // If it's a 401, the refresh token is invalid
      if (error.response?.status === 401) {
        throw new Error('Refresh token is invalid or expired');
      }
      throw error;
    }
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      console.log('[API POST]', { url, data: data ? '***' : undefined });
      const response: AxiosResponse<T> = await this.client.post(url, data, config);
      console.log('[API POST] Success', { url, status: response.status, hasData: !!response.data });
      return response.data;
    } catch (error: any) {
      console.error('[API POST] Error', { url, error: error.message, status: error.response?.status });
      throw error;
    }
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url, config);
    return response.data;
  }
}

export const apiClient = new ApiClient();
